import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { createApiOptions } from "@shared-ui/utils/api";
import { useRootLoader } from "@/hooks/useRootLoader";
import { usePageVisible } from "@/hooks/usePageVisible";
import { API_NOTIFICATIONS } from "@/utils/const";
import { calcBadgeCount } from "@/utils/notificationBadge";
import { usePreferences } from "@/contexts/PreferencesContext";
import type {
  DDBNotification,
  NotificationsResponse,
} from "@shared-domain/unizo/contract";

const POLL_INTERVAL_MS = 30_000;
const RECORDING_PATH_REGEX = /^\/(recording|speed-recording)(\/|$)/;

/**
 * 新着通知の polling フック。
 *
 * - 30 秒間隔で `/notification?limit=50` を fetch し、未読件数 (badgeCount) を更新する。
 * - 直前 fetch 以降に作成された未読通知が現れたら `newNotification` に最新1件を出力する（Toast 表示用）。
 * - 録音系画面（`/recording`, `/speed-recording`）配下では Toast 抑制（polling は継続）。
 * - PRODUCT_DEV-652: タブが非表示の間は polling を止め、表示に復帰した時点で即時 fetch する。
 *
 * 抽象フック化により、将来 Push 通知へ差し替える場合も interface を維持して内部実装のみ
 * 入れ替え可能（PRODUCT_DEV-386: 日暮さん 4/5 コメント要件）。
 */
export function useNewNotifications(): {
  badgeCount: number;
  newNotification: DDBNotification | null;
  dismissNewNotification: () => void;
} {
  const { auth } = useRootLoader();
  const location = useLocation();
  const visible = usePageVisible();

  // PRODUCT_DEV-699: 件数ではなく通知そのものを持ち、バッジ件数は「最後に見た時刻」と
  // 突き合わせて**その場で導出**する。以前は fetch 時に件数を確定していたため、
  // ①ベルを押しても次の poll（最大30秒）までバッジが消えない
  // ②「最後に見た時刻」がサーバから届く前に件数を確定すると、過去の通知を全部
  //   未読として数えた膨れた数が出る（設定をサーバへ移して初めて問題になる）
  // の2つが起きる。導出にすればどちらも起きない。
  const [notifications, setNotifications] = useState<DDBNotification[]>([]);
  const [newNotification, setNewNotification] =
    useState<DDBNotification | null>(null);

  const { values, isResolved } = usePreferences();
  const viewedAt = values.notificationViewedAt ?? null;

  /**
   * バッジを出してよいか。**「最後に見た時刻」が分かるまでは数えない。**
   * 分からないまま数えると、過去に見たはずの通知まで未読に含めた数を一瞬出してしまう。
   * 取得が済んでいれば値が無くても数えてよい（＝本当に一度も見ていない）。
   */
  const canCount = isResolved || viewedAt !== null;

  const badgeCount = useMemo(
    () => (canCount ? calcBadgeCount(notifications, viewedAt) : 0),
    [canCount, notifications, viewedAt],
  );

  // location を ref で参照することで、画面遷移ごとに polling リセットしない
  const locationPathRef = useRef(location.pathname);
  useEffect(() => {
    locationPathRef.current = location.pathname;
  }, [location.pathname]);

  // 前回 fetch 時の最大 createdAt（新着判定用）。初回 fetch では Toast 発火させない。
  const lastSeenCreatedAtRef = useRef<string | null>(null);
  const initializedRef = useRef(false);

  const tenantId = auth?.currentTenantId;

  useEffect(() => {
    if (!tenantId) {
      setNotifications([]);
      setNewNotification(null);
      lastSeenCreatedAtRef.current = null;
      initializedRef.current = false;
      return;
    }
    // PRODUCT_DEV-652: 裏タブではポーリングしない。バッジも Toast も見えないので
    // 失うものは無く、ログイン中ずっと 30 秒ごとに全ユーザぶん飛んでいたリクエストが消える。
    // 表示に復帰したら下の即時 poll() で追いつく（lastSeenCreatedAtRef は ref なので
    // 保持され、離席中に届いた通知の Toast も正しく判定される）。
    if (!visible) return;

    let cancelled = false;
    const abortController = new AbortController();

    const poll = async () => {
      try {
        const url = `${API_NOTIFICATIONS}?tenantId=${tenantId}&limit=50`;
        const options = await createApiOptions({
          method: "GET",
          headers: { "Content-Type": "application/json" },
          signal: abortController.signal,
        });
        const response = await fetch(url, options);
        // PRODUCT_DEV-902: **裏で回るポーリングなので 403 でも画面を飛ばさない。**
        // 録音中や入力中に突然 `/403` へ移ると、そのほうが害が大きい。
        // 弾かれていることは、利用者が何か操作した時点で表の通信が知らせる。
        if (cancelled || !response.ok) return;
        const result: NotificationsResponse = await response.json();
        const notifications = result.notifications ?? [];

        setNotifications(notifications);

        // 最新の通知を抽出（createdAt 降順）
        const latest = notifications.reduce<DDBNotification | null>(
          (acc, n) => {
            if (!acc || n.createdAt > acc.createdAt) return n;
            return acc;
          },
          null,
        );

        const previousMaxCreatedAt = lastSeenCreatedAtRef.current;
        if (latest) {
          lastSeenCreatedAtRef.current = latest.createdAt;
        }

        // 初回 fetch では Toast 発火しない（マウント直後のノイズ防止）
        if (!initializedRef.current) {
          initializedRef.current = true;
          return;
        }

        if (
          previousMaxCreatedAt &&
          latest &&
          !latest.isRead &&
          latest.createdAt > previousMaxCreatedAt &&
          !RECORDING_PATH_REGEX.test(locationPathRef.current)
        ) {
          setNewNotification(latest);
        }
      } catch {
        // ネットワークエラーは次回 poll で復帰
      }
    };

    // 初回（およびタブ復帰時）は即時 fetch + その後 30s 間隔
    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      abortController.abort();
      clearInterval(intervalId);
    };
  }, [tenantId, visible]);

  const dismissNewNotification = useCallback(() => {
    setNewNotification(null);
  }, []);

  return { badgeCount, newNotification, dismissNewNotification };
}
